import { motion } from "framer-motion";
import { TrendingUp, PieChart, Users, Wallet, ArrowUpRight } from "lucide-react";
import { formatBRL } from "@/data/financeData";

const STYLES = {
  blue: { icon: "text-blue-600", bg: "bg-blue-50", ring: "ring-blue-100" },
  emerald: { icon: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-100" },
  indigo: { icon: "text-indigo-600", bg: "bg-indigo-50", ring: "ring-indigo-100" },
  amber: { icon: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-100" },
};

const Card = ({ card, index }) => {
  const s = STYLES[card.color];
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06, ease: "easeOut" }}
      data-testid={`fin-kpi-${card.id}`}
      className="group relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)]"
    >
      <div className="flex items-start justify-between">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${s.bg} ring-1 ${s.ring}`}>
          <card.icon className={`h-5 w-5 ${s.icon}`} strokeWidth={2.2} />
        </div>
        {card.badge && (
          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${card.badgeClass}`}>
            {card.badgeIcon && <card.badgeIcon className="h-3 w-3" />}
            {card.badge}
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {card.title}
        </p>
        <p
          data-testid={`fin-kpi-${card.id}-value`}
          className="font-mono-num mt-1 text-2xl font-bold text-slate-900"
        >
          {formatBRL(card.value)}
        </p>
        <p className="mt-0.5 text-xs text-slate-500">{card.subtitle}</p>
      </div>
    </motion.div>
  );
};

export const FinanceKpiGrid = ({ data }) => {
  const cards = [
    {
      id: "faturamento",
      title: "Faturamento Bruto",
      icon: TrendingUp,
      color: "blue",
      value: data.faturamentoBruto,
      subtitle: "Receita total no período",
      badge: `+${data.faturamentoVariacao}% vs. anterior`,
      badgeIcon: ArrowUpRight,
      badgeClass: "bg-emerald-50 text-emerald-600",
    },
    {
      id: "lucro",
      title: "Lucro Líquido Real (DRE)",
      icon: PieChart,
      color: "emerald",
      value: data.lucroLiquido,
      subtitle: `Margem de ${data.margem}%`,
      badge: `${data.margem}%`,
      badgeClass: "bg-emerald-50 text-emerald-600",
    },
    {
      id: "repasses",
      title: "Total de Repasses a Médicos",
      icon: Users,
      color: "indigo",
      value: data.repasses,
      subtitle: "Colaboradores no período",
    },
    {
      id: "caixa",
      title: "Previsão de Caixa (30 dias)",
      icon: Wallet,
      color: "amber",
      value: data.previsaoCaixa,
      subtitle: "Status: Saudável",
      badge: "Saudável",
      badgeClass: "bg-emerald-50 text-emerald-600",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {cards.map((c, i) => (
        <Card key={c.id} card={c} index={i} />
      ))}
    </div>
  );
};
